/**
 * InheritedFromATBanner — Bandeau affiché en haut de chaque permis détaillé
 * en mode compact (permis enfant d'une AT) pour montrer les champs auto-importés
 * depuis l'AT parente : dates, description, lieu, entreprise, N° PP.
 * Pas de re-saisie possible — affichage en lecture seule.
 * Affiche aussi le statut de l'AT parente (brouillon / revise / approuve / rejete / cloture).
 */
import { Link2, ExternalLink } from 'lucide-react';

const STATUS_BADGE = {
  brouillon:  { label: 'Brouillon',     classes: 'bg-stone-300 text-stone-800' },
  revise:     { label: 'En validation', classes: 'bg-amber-300 text-amber-900' },
  approuve:   { label: 'Validée',       classes: 'bg-green-500 text-white' },
  rejete:     { label: 'Refusée',       classes: 'bg-red-500 text-white' },
  cloture:    { label: 'Clôturée',      classes: 'bg-stone-700 text-white' },
};

function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wider text-stone-500 mb-0.5">{label}</div>
      <div className={`border border-stone-300 bg-white/60 px-2 py-1.5 text-xs ${mono ? 'font-mono' : ''} min-h-[1.95rem]`}>
        {value || <span className="text-stone-400 italic">—</span>}
      </div>
    </div>
  );
}

export default function InheritedFromATBanner({ dateDebut, dateFin, titre, lieu, entreprise, ppNumero, ppValidite, ppStatus, parentId, parentStatus, parentNumero }) {
  const badge = parentStatus ? (STATUS_BADGE[parentStatus] || { label: parentStatus, classes: 'bg-stone-300 text-stone-800' }) : null;

  // Badge PdP : émission + statut (Valide / Non valide)
  // Convention métier : PdP typiquement valide 1 an depuis sa date d'émission
  let ppValiditeBadge = null;
  if (ppValidite || ppStatus) {
    let dStr = '';
    let endValidite = null;
    if (ppValidite) {
      try {
        const d = new Date(ppValidite);
        dStr = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
        endValidite = new Date(d);
        endValidite.setFullYear(endValidite.getFullYear() + 1); // PdP = valide 1 an
      } catch (e) { /* ignore */ }
    }
    const isInvalid = (ppStatus || '').toLowerCase().includes('non');
    const endWork = dateFin ? new Date(dateFin) : null;
    const expiresBeforeEnd = endValidite && endWork && endValidite < endWork;
    let cls = 'bg-emerald-600 text-white';
    let txt = '';
    if (isInvalid) {
      cls = 'bg-red-600 text-white';
      txt = `⚠ PdP NON VALIDE${dStr ? ` (émis ${dStr})` : ''}`;
    } else if (expiresBeforeEnd) {
      cls = 'bg-red-600 text-white';
      const endStr = `${String(endValidite.getDate()).padStart(2, '0')}/${String(endValidite.getMonth() + 1).padStart(2, '0')}/${endValidite.getFullYear()}`;
      txt = `⚠ PdP expire le ${endStr} — AVANT fin travaux`;
    } else if (endValidite) {
      const endStr = `${String(endValidite.getDate()).padStart(2, '0')}/${String(endValidite.getMonth() + 1).padStart(2, '0')}/${endValidite.getFullYear()}`;
      txt = `✓ PdP valide jusqu'au ${endStr}`;
    } else if (dStr) {
      txt = `✓ PdP émis le ${dStr}`;
    }
    if (txt) {
      ppValiditeBadge = (
        <div className={`mt-1 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 py-1 ${cls}`} data-testid="pp-validite-badge">
          {txt}
        </div>
      );
    }
  }

  return (
    <div className="bg-blue-50 border-2 border-blue-300 p-3 space-y-3" data-testid="inherited-from-at-banner">
      <div className="flex items-center flex-wrap gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-blue-900">
        <Link2 className="w-3.5 h-3.5" />
        <span>Données auto-importées de l'AT</span>
        {parentNumero && <span className="font-mono text-blue-700 normal-case tracking-normal" data-testid="parent-at-numero">{parentNumero}</span>}
        {badge && (
          <span
            data-testid="parent-at-status"
            className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${badge.classes}`}
          >
            {badge.label}
          </span>
        )}
        {parentId && (
          <a
            href={`/permits/new?edit=${parentId}`}
            target="_blank"
            rel="noreferrer"
            data-testid="link-to-parent-at"
            className="ml-auto inline-flex items-center gap-1 px-2 py-1 bg-blue-700 hover:bg-blue-800 transition-colors text-white text-[9px] uppercase tracking-wider"
          >
            <ExternalLink className="w-3 h-3" />
            Voir l'AT parente
          </a>
        )}
        <span className={`${parentId ? '' : 'ml-auto'} px-1.5 py-0.5 bg-blue-200 text-blue-900 text-[9px] uppercase tracking-wider`}>lecture seule</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Field label="Date début" value={dateDebut} mono />
        <Field label="Date fin" value={dateFin} mono />
        <Field label="Entreprise" value={entreprise || 'Travaux internes'} />
        <Field label="N° Plan de Prévention" value={ppNumero} mono />
      </div>
      {ppValiditeBadge}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="Description du travail" value={titre} />
        <Field label="Localisation" value={lieu} />
      </div>
    </div>
  );
}
